import { Badge } from '@/components/ui-kit/badge';
import { Card, CardContent } from '@/components/ui-kit/card';
import { cn } from '@/lib/utils';
import type { Template } from '@/modules/templates/types/templates.types';
import { FileText } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Organization } from '../../types/org.types';
import ContractsTable, {
  type ContractsTableColumn,
} from '../../contracts/components/contracts-table';
import { NewTemplateDialog } from '../new-templates-dialog';
import { TemplateCard } from '../template-card';
import { TemplateRowActions } from '../template-row-actions';
import { ORG_TEMPLATES_PAGE_SIZE, type OrgTemplatesController } from './use-org-templates';

const formatDate = (iso?: string) => {
  if (!iso) return undefined;
  try {
    return new Date(iso).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

interface OrgTemplatesListProps {
  controller: OrgTemplatesController;
  org: Organization;
  orgSlug: string;
  canManage: boolean;
  isAdmin: boolean;
}

/**
 * Org Templates tab body: empty state, desktop data table, mobile card list,
 * and the "templates used / max" footer.
 */
export function OrgTemplatesList({
  controller,
  org,
  orgSlug,
  canManage,
  isAdmin,
}: OrgTemplatesListProps) {
  const { t } = useTranslation();
  const { templates, totalCount, totalPages, page, setPage, isLoading, isEmptyState } = controller;

  const columns = useMemo<ContractsTableColumn<Template>[]>(
    () => [
      {
        id: 'title',
        header: t('ORGCONTRACTS_COLUMNS_TITLE'),
        cell: (template) => (
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 shrink-0 rounded-lg bg-selise-blue/10 flex items-center justify-center">
              <FileText className="h-5 w-5 text-selise-blue" />
            </div>
            <div className="min-w-0 space-y-1">
              <p className="max-w-90 truncate text-[15px] font-semibold leading-5 text-[hsl(var(--fg))]">
                {template.title}
              </p>
              <p className="truncate text-sm text-globe-grey">{template.slug}</p>
            </div>
          </div>
        ),
      },
      {
        id: 'createdDate',
        header: t('ORGCONTRACTS_COLUMNS_CREATEDDATE'),
        cell: (template) => (
          <p className="text-[15px] font-semibold text-[hsl(var(--fg))]">
            {formatDate(template.createdAt) ?? '-'}
          </p>
        ),
      },
      {
        id: 'status',
        header: t('ORGCONTRACTS_COLUMNS_STATUS'),
        cell: (template) => (
          <Badge variant={template.available ? 'success' : 'secondary'} className="gap-1.5">
            <span
              className={cn(
                'size-1.5 rounded-full',
                template.available ? 'bg-green-500' : 'bg-globe-grey'
              )}
            />
            {template.available ? t('ORGTEMPLATES_STATUSACTIVE') : t('ORGTEMPLATES_STATUSDRAFT')}
          </Badge>
        ),
      },
      {
        id: 'action',
        header: t('ORGCONTRACTS_COLUMNS_ACTION'),
        headerClassName: 'text-right',
        cellClassName: 'text-right',
        cell: (template) => (
          <TemplateRowActions
            template={template}
            orgSlug={orgSlug}
            canManage={canManage}
            isAdmin={isAdmin}
          />
        ),
      },
    ],
    [t, orgSlug, canManage, isAdmin]
  );

  return (
    <div className="space-y-4">
      {isEmptyState ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileText className="h-12 w-12 text-globe-grey-500 mb-4" />
            <h3 className="text-lg font-semibold mb-2">{t('ORGTEMPLATES_EMPTYTITLE')}</h3>
            <p className="text-globe-grey text-center mb-4">{t('ORGTEMPLATES_EMPTYDESCRIPTION')}</p>
            {canManage && (
              <NewTemplateDialog orgSlug={orgSlug} orgName={org.Name} orgId={org.BlocksOrgId} />
            )}
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Desktop: contracts-style data table */}
          <div className="hidden md:block">
            <ContractsTable
              columns={columns}
              data={templates}
              rowKey={(template) => template.id}
              isLoading={isLoading}
              emptyMessage={t('ORGTEMPLATES_EMPTYTITLE')}
              pagination={{
                page,
                limit: ORG_TEMPLATES_PAGE_SIZE,
                total: totalCount,
                totalPages,
              }}
              onPageChange={setPage}
            />
          </div>

          {/* Mobile: card layout */}
          <div className="grid grid-cols-1 gap-4 md:hidden">
            {templates.map((template) => (
              <TemplateCard
                key={template.id}
                template={template}
                orgSlug={orgSlug}
                canManage={canManage}
              />
            ))}
          </div>
        </>
      )}

      {/* Template limits */}
      <div className="text-sm text-globe-grey text-center">
        {t('ORGTEMPLATES_TEMPLATESUSED', {
          used: totalCount,
          total: org.MaxTemplates,
        })}
      </div>
    </div>
  );
}
